import React, { useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { DragDropProvider, DragEndEvent, KeyboardSensor, PointerSensor } from '@dnd-kit/react'
import { PointerActivationConstraints } from '@dnd-kit/dom'

import Spinner from 'components/Spinner'

import useDashboardLayout from 'hooks/useDashboardLayout'

import { CardId } from 'pages/Dashboard/cardRegistry'
import CustomizeToolbar, { RemovedCardOption } from 'pages/Dashboard/CustomizeToolbar'
import DraggableCard, { ADDED_FLASH_DURATION_MS } from 'pages/Dashboard/DraggableCard'
import EndDropZone, { END_DROP_ZONE_ID } from 'pages/Dashboard/EndDropZone'

import { NoData, screenSize } from 'styles/shared'

const MAX_COLUMNS = 2

const sensors = [
  PointerSensor.configure({
    activationConstraints: [new PointerActivationConstraints.Distance({ value: 5 })]
  }),
  KeyboardSensor
]

const DraggableDashboard = () => {
  const {
    addedCards,
    removedCards,
    isLayoutLoaded,
    isCustomizing,
    isSaving,
    startCustomize,
    reorder,
    removeCard,
    addCard,
    save,
    cancel,
    enabledFeatures,
    cardRegistry
  } = useDashboardLayout()
  const isHehmEnabled = enabledFeatures.includes('hehm')

  const lastRowHasEmptyCell = useMemo(() => {
    let endColumn = 0

    for (const id of addedCards) {
      const span = cardRegistry[id].colSpan

      if (endColumn + span > MAX_COLUMNS) {
        endColumn = span
      } else {
        endColumn += span
      }
    }

    return endColumn === 1
  }, [addedCards, cardRegistry])

  const removedCardOptions = useMemo<RemovedCardOption[]>(() =>
    removedCards.map((id) => ({ id, title: cardRegistry[id].Component.cardTitle })),
  [removedCards, cardRegistry])

  const [flashingCardId, setFlashingCardId] = useState<CardId | null>(null)
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const addCardWithFlash = useCallback((id: CardId) => {
    addCard(id)
    setFlashingCardId(id)

    if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current)

    flashTimeoutRef.current = setTimeout(() => setFlashingCardId(null), ADDED_FLASH_DURATION_MS)
  }, [addCard])

  const onDragEnd = useCallback((event: DragEndEvent) => {
    if (event.canceled) return

    const { source, target } = event.operation

    if (source && target && source.id !== target.id) {
      reorder(source.id as CardId, target.id as CardId | typeof END_DROP_ZONE_ID)
    }
  }, [reorder])

  if (!isLayoutLoaded) return <Spinner variant="page" />

  return (
    <>
      {isHehmEnabled && (
        <S.ToolbarRow>
          <CustomizeToolbar
            isCustomizing={isCustomizing}
            isSaving={isSaving}
            removedCards={removedCardOptions}
            onCustomizeCallback={startCustomize}
            onAddCardCallback={addCardWithFlash}
            onSaveCallback={save}
            onCancelCallback={cancel}
          />
        </S.ToolbarRow>
      )}
      <DragDropProvider sensors={sensors} onDragEnd={onDragEnd}>
        {addedCards.length === 0 ? (
          <NoData>
            <h1>Empty Dashboard</h1>
            <h2>Click Customize to add cards.</h2>
          </NoData>
        ) : (
          <S.Grid>
            {addedCards.map((id) => (
              <DraggableCard
                key={id}
                id={id}
                config={cardRegistry[id]}
                isCustomizing={isCustomizing}
                isFlashing={flashingCardId === id}
                onRemoveCallback={removeCard}
              />
            ))}
            <EndDropZone fillsEmptyCell={lastRowHasEmptyCell} />
          </S.Grid>
        )}
      </DragDropProvider>
    </>
  )
}

export default DraggableDashboard

const gapWidth = '10px'

const S = {
  ToolbarRow: styled.div`
    display: flex;
    justify-content: flex-end;
    padding: 0 ${gapWidth};
    margin-bottom: 4px;
  `,
  Grid: styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    grid-auto-rows: auto;
    align-items: stretch;
    gap: ${gapWidth};
    padding: ${gapWidth};

    @media ${screenSize.large} {
      grid-template-columns: minmax(0, 1fr);
    }
  `
}
